import React, { useState, useEffect, useRef, useCallback } from 'react';
import YouTube from 'react-youtube';
import { useSpotifyPlayer } from '../../contexts/SpotifyPlayerContext';
import {
  Play, Pause, SkipForward, SkipBack, Volume2, VolumeX,
  X, Music, Loader2, AlertCircle, Wifi, WifiOff
} from 'lucide-react';
import './SetPreviewPlayer.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SetPreviewPlayer = ({ tracks, onClose }) => {
  // Spotify Player Context
  const spotifyPlayer = useSpotifyPlayer();
  const spotifyToken = localStorage.getItem('spotify_access_token');
  const isSpotifyConnected = !!spotifyToken && spotifyToken !== 'mock_token_for_testing';
  const isSpotifyReady = spotifyPlayer?.isReady && spotifyPlayer?.isPremium;
  
  // Player state
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [volume, setVolume] = useState(0.7);
  const [isMuted, setIsMuted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [crossfadeProgress, setCrossfadeProgress] = useState(0);
  
  // Playback source state
  const [playbackSource, setPlaybackSource] = useState(null); // 'spotify' | 'youtube' | 'preview'
  const [currentYouTubeId, setCurrentYouTubeId] = useState(null);
  const [showSpotifyPrompt, setShowSpotifyPrompt] = useState(false);
  const [audioStatus, setAudioStatus] = useState({}); // Track audio availability per track
  
  // Refs
  const audioRef = useRef(null);
  const nextAudioRef = useRef(null);
  const youtubePlayerRef = useRef(null);
  const progressIntervalRef = useRef(null);
  const crossfadeTimeoutRef = useRef(null);
  const timeTrackingRef = useRef(null);

  // Constants
  const CROSSFADE_DURATION = 4000; // 4 seconds crossfade
  const TRACK_PLAY_DURATION = 25000; // 25 seconds per track for previews
  const FULL_TRACK_CROSSFADE_OFFSET = 10000; // Start crossfade 10s before end for full tracks

  // Get current and next track
  const currentTrack = tracks[currentTrackIndex];
  const nextTrack = tracks[currentTrackIndex + 1];

  // Determine audio availability for each track
  const getAudioType = (track) => {
    if (!track) return 'none';
    if (track.spotify_uri && isSpotifyReady) return 'full';
    if (track.youtube_id) return 'full';
    if (track.preview_url) return '30s';
    return 'none';
  };

  // Count playable tracks
  const playableTracks = tracks.filter(t => getAudioType(t) !== 'none');

  // Initialize audio elements
  useEffect(() => {
    audioRef.current = new Audio();
    nextAudioRef.current = new Audio();
    
    audioRef.current.volume = volume;
    nextAudioRef.current.volume = 0;

    return () => {
      cleanup();
    };
  }, []);

  // Cleanup function
  const cleanup = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
    }
    if (nextAudioRef.current) {
      nextAudioRef.current.pause();
      nextAudioRef.current.src = '';
    }
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
    }
    if (crossfadeTimeoutRef.current) {
      clearTimeout(crossfadeTimeoutRef.current);
    }
    if (timeTrackingRef.current) {
      clearInterval(timeTrackingRef.current);
    }
    if (spotifyPlayer) {
      spotifyPlayer.pause().catch(() => {});
    }
  };

  // Update volume
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = isMuted ? 0 : volume;
    }
    if (playbackSource === 'spotify' && spotifyPlayer) {
      spotifyPlayer.setVolume(isMuted ? 0 : volume).catch(() => {});
    }
  }, [volume, isMuted, playbackSource, spotifyPlayer]);

  // Check Spotify connection on mount
  useEffect(() => {
    const hasShownPrompt = sessionStorage.getItem('crate_spotify_prompt_shown');
    if (!isSpotifyConnected && !hasShownPrompt && playableTracks.length > 0) {
      // Check if any tracks could benefit from Spotify Premium
      const hasSpotifyTracks = tracks.some(t => t.spotify_uri || t.source === 'spotify');
      if (hasSpotifyTracks) {
        setShowSpotifyPrompt(true);
        sessionStorage.setItem('crate_spotify_prompt_shown', 'true');
      }
    }
  }, [isSpotifyConnected, tracks]);

  // Search Spotify for track
  const searchSpotify = async (track) => {
    try {
      const query = `${track.artist || ''} ${track.title || ''}`.trim();
      const response = await fetch(`${BACKEND_URL}/api/spotify/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query, limit: 1 })
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.tracks && data.tracks.length > 0) {
          return {
            uri: data.tracks[0].uri,
            duration_ms: data.tracks[0].duration_ms
          };
        }
      }
    } catch (error) {
      console.error('Spotify search error:', error);
    }
    return null;
  };

  // Search YouTube for track
  const searchYouTube = async (track) => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/youtube/crates/search?artist=${encodeURIComponent(track.artist || '')}&title=${encodeURIComponent(track.title || '')}`
      );
      if (response.ok) {
        const data = await response.json();
        if (data.found && data.video_id) {
          return data.video_id;
        }
      }
    } catch (error) {
      console.error('YouTube search error:', error);
    }
    return null;
  };

  // Load and play track with hybrid fallback
  const loadAndPlayTrack = useCallback(async (trackIndex) => {
    if (trackIndex >= tracks.length) {
      // End of set
      setIsPlaying(false);
      setCurrentTrackIndex(0);
      setPlaybackSource(null);
      return;
    }

    const track = tracks[trackIndex];
    if (!track) {
      loadAndPlayTrack(trackIndex + 1);
      return;
    }

    setIsLoading(true);
    setError(null);
    setCurrentTrackIndex(trackIndex);
    setCurrentYouTubeId(null);

    // Stop current playback
    if (playbackSource === 'spotify' && spotifyPlayer) {
      await spotifyPlayer.pause().catch(() => {});
    }
    if (playbackSource === 'preview' && audioRef.current) {
      audioRef.current.pause();
    }
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
    }

    try {
      // STEP 1: Try Spotify (if Premium connected)
      if (isSpotifyReady) {
        let spotifyUri = track.spotify_uri;
        
        // If no URI stored, search for it
        if (!spotifyUri) {
          const spotifyResult = await searchSpotify(track);
          if (spotifyResult) {
            spotifyUri = spotifyResult.uri;
          }
        }
        
        if (spotifyUri) {
          console.log('▶️ Playing via Spotify:', spotifyUri);
          const success = await spotifyPlayer.play(spotifyUri);
          if (success) {
            setPlaybackSource('spotify');
            setIsPlaying(true);
            setIsLoading(false);
            startTimeTracking('spotify', track.duration_ms || 180000);
            return;
          }
        }
      }

      // STEP 2: Try YouTube
      let youtubeId = track.youtube_id;
      if (!youtubeId) {
        youtubeId = await searchYouTube(track);
      }
      
      if (youtubeId) {
        console.log('▶️ Playing via YouTube:', youtubeId);
        setCurrentYouTubeId(youtubeId);
        setPlaybackSource('youtube');
        setIsLoading(false);
        // YouTube will auto-start via onReady
        return;
      }

      // STEP 3: Try preview URL
      if (track.preview_url) {
        console.log('▶️ Playing via preview URL');
        audioRef.current.src = track.preview_url;
        audioRef.current.volume = isMuted ? 0 : volume;
        await audioRef.current.play();
        setPlaybackSource('preview');
        setIsPlaying(true);
        setIsLoading(false);
        startTimeTracking('preview', 30000); // 30 second previews
        return;
      }

      // STEP 4: No audio available - skip to next
      console.log('⚠️ No audio available for:', track.title);
      setError(`No audio available for "${track.title}"`);
      setIsLoading(false);
      setTimeout(() => loadAndPlayTrack(trackIndex + 1), 1500);

    } catch (err) {
      console.error('Error playing track:', err);
      setError('Failed to play track');
      setIsLoading(false);
      setTimeout(() => loadAndPlayTrack(trackIndex + 1), 1500);
    }
  }, [tracks, isSpotifyReady, spotifyPlayer, volume, isMuted, playbackSource]);

  // Time tracking for progress bar
  const startTimeTracking = (source, duration) => {
    if (timeTrackingRef.current) {
      clearInterval(timeTrackingRef.current);
    }
    
    let elapsed = 0;
    const interval = 100;
    const trackDuration = source === 'preview' ? TRACK_PLAY_DURATION : duration;
    
    timeTrackingRef.current = setInterval(() => {
      elapsed += interval;
      const trackProgress = (elapsed / trackDuration) * 100;
      setProgress(Math.min(trackProgress, 100));

      // Auto-advance for previews
      if (source === 'preview' && elapsed >= trackDuration) {
        clearInterval(timeTrackingRef.current);
        loadAndPlayTrack(currentTrackIndex + 1);
      }
    }, interval);
  };

  // YouTube event handlers
  const onYouTubeReady = (event) => {
    youtubePlayerRef.current = event.target;
    event.target.setVolume((isMuted ? 0 : volume) * 100);
    event.target.playVideo();
  };

  const onYouTubeStateChange = (event) => {
    const YT = window.YT;
    if (event.data === YT?.PlayerState?.PLAYING) {
      setIsPlaying(true);
      setIsLoading(false);
      const duration = youtubePlayerRef.current?.getDuration() * 1000 || 180000;
      startTimeTracking('youtube', duration);
    } else if (event.data === YT?.PlayerState?.ENDED) {
      loadAndPlayTrack(currentTrackIndex + 1);
    } else if (event.data === YT?.PlayerState?.PAUSED) {
      setIsPlaying(false);
      if (timeTrackingRef.current) {
        clearInterval(timeTrackingRef.current);
      }
    }
  };

  const onYouTubeError = () => {
    console.error('YouTube playback error');
    setError('YouTube playback failed');
    setTimeout(() => loadAndPlayTrack(currentTrackIndex + 1), 1500);
  };

  // Play/Pause toggle
  const togglePlayPause = async () => {
    if (isPlaying) {
      // Pause
      if (playbackSource === 'spotify' && spotifyPlayer) {
        await spotifyPlayer.pause();
      } else if (playbackSource === 'youtube' && youtubePlayerRef.current) {
        youtubePlayerRef.current.pauseVideo();
      } else if (playbackSource === 'preview' && audioRef.current) {
        audioRef.current.pause();
      }
      if (timeTrackingRef.current) {
        clearInterval(timeTrackingRef.current);
      }
      setIsPlaying(false);
    } else {
      // Play or resume
      if (!playbackSource) {
        // Start from beginning
        loadAndPlayTrack(currentTrackIndex);
      } else if (playbackSource === 'spotify' && spotifyPlayer) {
        await spotifyPlayer.resume();
        setIsPlaying(true);
      } else if (playbackSource === 'youtube' && youtubePlayerRef.current) {
        youtubePlayerRef.current.playVideo();
      } else if (playbackSource === 'preview' && audioRef.current) {
        await audioRef.current.play();
        setIsPlaying(true);
      }
    }
  };

  // Skip controls
  const skipNext = () => {
    if (currentTrackIndex < tracks.length - 1) {
      loadAndPlayTrack(currentTrackIndex + 1);
    }
  };

  const skipPrev = () => {
    if (currentTrackIndex > 0) {
      loadAndPlayTrack(currentTrackIndex - 1);
    }
  };

  // Calculate total progress through set
  const totalProgress = ((currentTrackIndex + progress / 100) / tracks.length) * 100;

  // Get source badge
  const getSourceBadge = () => {
    if (playbackSource === 'spotify') return { icon: '🎵', label: 'Spotify Premium', color: '#1DB954' };
    if (playbackSource === 'youtube') return { icon: '📹', label: 'YouTube', color: '#FF0000' };
    if (playbackSource === 'preview') return { icon: '▶️', label: '30s Preview', color: '#facc15' };
    return null;
  };

  const sourceBadge = getSourceBadge();

  // Spotify Premium Prompt Modal
  if (showSpotifyPrompt) {
    return (
      <div className="set-preview-player spotify-prompt">
        <div className="prompt-content">
          <div className="prompt-icon">🎵</div>
          <h3>Enhance Your Preview Experience</h3>
          <p>Connect Spotify Premium for full track playback instead of 30-second clips.</p>
          <div className="prompt-actions">
            <button 
              className="prompt-btn primary"
              onClick={() => {
                setShowSpotifyPrompt(false);
                // Trigger Spotify connect flow
                window.location.href = `${BACKEND_URL}/api/spotify/login?redirect=${encodeURIComponent(window.location.href)}`;
              }}
            >
              Connect Spotify
            </button>
            <button 
              className="prompt-btn secondary"
              onClick={() => setShowSpotifyPrompt(false)}
            >
              Continue with Previews
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (playableTracks.length === 0) {
    return (
      <div className="set-preview-player">
        <div className="preview-error">
          <AlertCircle size={24} />
          <span>No tracks with audio available</span>
          <p className="error-hint">
            Add tracks from Discovery to get audio previews, or connect Spotify Premium.
          </p>
          <button onClick={onClose}>Close</button>
        </div>
      </div>
    );
  }

  return (
    <div className="set-preview-player" data-testid="set-preview-player">
      {/* Close button */}
      <button className="close-preview-btn" onClick={onClose}>
        <X size={18} />
      </button>

      {/* Hidden YouTube Player */}
      {currentYouTubeId && (
        <div style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}>
          <YouTube
            videoId={currentYouTubeId}
            opts={{
              height: '1',
              width: '1',
              playerVars: {
                autoplay: 1,
                controls: 0,
              },
            }}
            onReady={onYouTubeReady}
            onStateChange={onYouTubeStateChange}
            onError={onYouTubeError}
          />
        </div>
      )}

      {/* Now Playing */}
      <div className="now-playing">
        <div className="now-playing-artwork">
          {currentTrack?.artwork_url ? (
            <img src={currentTrack.artwork_url} alt="" />
          ) : (
            <div className="no-artwork"><Music size={24} /></div>
          )}
          {isLoading && (
            <div className="loading-overlay">
              <Loader2 size={24} className="spinning" />
            </div>
          )}
        </div>
        
        <div className="now-playing-info">
          <div className="now-playing-label">
            {crossfadeProgress > 0 ? 'Crossfading...' : 'Now Playing'}
            {sourceBadge && (
              <span 
                className="source-badge" 
                style={{ backgroundColor: `${sourceBadge.color}20`, color: sourceBadge.color }}
              >
                {sourceBadge.icon} {sourceBadge.label}
              </span>
            )}
          </div>
          <div className="now-playing-title">{currentTrack?.title || 'Unknown Track'}</div>
          <div className="now-playing-artist">{currentTrack?.artist || 'Unknown Artist'}</div>
          <div className="track-position">
            Track {currentTrackIndex + 1} of {tracks.length}
            {playableTracks.length < tracks.length && (
              <span className="playable-count"> ({playableTracks.length} playable)</span>
            )}
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="preview-error-inline">
          <AlertCircle size={14} />
          <span>{error}</span>
        </div>
      )}

      {/* Progress Bar */}
      <div className="preview-progress">
        <div className="progress-bar">
          <div 
            className="progress-fill" 
            style={{ width: `${totalProgress}%` }}
          />
        </div>
        <div className="progress-labels">
          <span>{currentTrackIndex + 1} / {tracks.length}</span>
          <span>Set Preview</span>
        </div>
      </div>

      {/* Controls */}
      <div className="preview-controls">
        <button 
          className="control-btn"
          onClick={skipPrev}
          disabled={currentTrackIndex === 0 || isLoading}
        >
          <SkipBack size={20} />
        </button>
        
        <button 
          className="control-btn play-pause"
          onClick={togglePlayPause}
          disabled={isLoading}
        >
          {isLoading ? (
            <Loader2 size={24} className="spinning" />
          ) : isPlaying ? (
            <Pause size={24} />
          ) : (
            <Play size={24} />
          )}
        </button>
        
        <button 
          className="control-btn"
          onClick={skipNext}
          disabled={currentTrackIndex >= tracks.length - 1 || isLoading}
        >
          <SkipForward size={20} />
        </button>

        <div className="volume-control">
          <button 
            className="control-btn volume-btn"
            onClick={() => setIsMuted(!isMuted)}
          >
            {isMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
          </button>
          <input
            type="range"
            min="0"
            max="1"
            step="0.1"
            value={isMuted ? 0 : volume}
            onChange={(e) => {
              setVolume(parseFloat(e.target.value));
              setIsMuted(false);
            }}
            className="volume-slider"
          />
        </div>
      </div>

      {/* Connection Status */}
      <div className="connection-status">
        {isSpotifyReady ? (
          <span className="status connected">
            <Wifi size={12} /> Spotify Premium
          </span>
        ) : (
          <span className="status disconnected">
            <WifiOff size={12} /> Preview Mode
          </span>
        )}
      </div>
    </div>
  );
};

export default SetPreviewPlayer;
